import React, { useState, useEffect, useCallback } from 'react';
import { motion } from 'framer-motion';
import { Bell, RefreshCw, Filter, Calendar, Tag } from 'lucide-react';
import DOMPurify from 'dompurify';

const StudioUpdates = () => {
  const [updates, setUpdates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [availableTags, setAvailableTags] = useState([]);
  const [selectedTag, setSelectedTag] = useState('');

  const fetchUpdates = useCallback(async () => {
    
    
    try {
      let url = `${process.env.REACT_APP_BACKEND_URL}/api/studio-portal/updates`;
      if (selectedTag) {
        url += `?tag=${selectedTag}`;
      }
      
      const response = await fetch(url, {
        credentials: 'include'
      });
      
      if (response.ok) {
        const data = await response.json();
        setUpdates(data.updates || []);
        setAvailableTags(data.available_tags || []);
      }
    } catch (err) {
      console.error('Failed to fetch updates:', err);
    } finally {
      setLoading(false);
    }
  }, [selectedTag]);

  useEffect(() => {
    fetchUpdates();
  }, [fetchUpdates]);

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="w-8 h-8 text-swp-ice animate-spin" />
      </div>
    );
  }

  return (
    <div className="p-6 lg:p-8" data-testid="studio-updates">
      {/* Header */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="mb-8"
      >
        <h1 className="text-3xl font-bold text-white font-display mb-2">Updates</h1>
        <p className="text-swp-white-ghost">Stay informed with the latest news and announcements</p>
      </motion.div>

      {/* Tag Filter */}
      {availableTags.length > 0 && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="mb-6"
        >
          <div className="flex items-center gap-2 flex-wrap">
            <Filter size={16} className="text-swp-white-ghost/70" />
            <button
              onClick={() => setSelectedTag('')}
              className={`px-3 py-1.5 text-sm rounded-sm transition-colors ${
                !selectedTag 
                  ? 'bg-swp-ice text-white' 
                  : 'bg-swp-surface text-swp-white-ghost hover:text-swp-white'
              }`}
            >
              All
            </button>
            {availableTags.map((tag) => (
              <button
                key={tag}
                onClick={() => setSelectedTag(tag)}
                className={`px-3 py-1.5 text-sm rounded-sm transition-colors capitalize ${
                  selectedTag === tag 
                    ? 'bg-swp-ice text-white' 
                    : 'bg-swp-surface text-swp-white-ghost hover:text-swp-white'
                }`}
              >
                {tag.replace('_', ' ')}
              </button>
            ))}
          </div>
        </motion.div>
      )}

      {/* Updates List */}
      {updates.length > 0 ? (
        <div className="space-y-4">
          {updates.map((update, idx) => (
            <motion.article
              key={update.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: idx * 0.05 }}
              className="bg-swp-surface border border-swp-border rounded-swp p-6 hover:border-swp-border transition-colors"
            >
              <div className="flex items-start justify-between gap-4 mb-3">
                <h2 className="text-xl text-white font-semibold">{update.title}</h2>
                <div className="flex items-center gap-2 text-swp-white-ghost/70 text-sm whitespace-nowrap">
                  <Calendar size={14} />
                  {formatDate(update.created_at)}
                </div>
              </div>
              
              {/* Tags */}
              {update.tags?.length > 0 && (
                <div className="flex flex-wrap gap-2 mb-4">
                  {update.tags.map((tag, tIdx) => (
                    <span 
                      key={tIdx} 
                      className="px-2 py-0.5 bg-swp-ice/10 text-swp-ice text-xs font-mono uppercase rounded"
                    >
                      {tag.replace('_', ' ')}
                    </span>
                  ))}
                </div>
              )}
              
              {/* Body */}
              <div 
                className="text-swp-white-ghost prose prose-invert prose-sm max-w-none"
                dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(update.body) }}
              />
            </motion.article>
          ))}
        </div>
      ) : (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-swp-surface border border-swp-border rounded-swp p-12 text-center"
        >
          <Bell size={48} className="text-swp-white-ghost/50 mx-auto mb-4" />
          <h2 className="text-xl text-white mb-2">No Updates Yet</h2>
          <p className="text-swp-white-ghost/70">
            {selectedTag 
              ? `No updates found with the "${selectedTag}" tag.`
              : "Check back later for news and announcements about your projects."}
          </p>
        </motion.div>
      )}
    </div>
  );
};

export default StudioUpdates;
